<template>
    <div class="contents">
        <Teleport to="body">
            <div
                v-if="show && props.content?.prominent"
                class="toast-prominent-effect"
                :class="`toast-prominent-effect--${props.content?.notificationType}`"
                aria-hidden="true"
            ></div>
        </Teleport>
        <div
            v-if="show"
            ref="toastEl"
            class="relative flex gap-4 items-center w-full max-w-xs p-5 pr-7 text-text-light rounded-lg shadow bg-background-accent"
            :class="{
                'border border-error bg-linear-185 from-red-100 to-30% to-white': props.content?.notificationType === 'error',
                'border border-success bg-linear-185 from-success/10 to-30% to-white': props.content?.notificationType === 'success'
            }"
            role="alert"
            @mouseover="hasMousedOver"
        >

        <svg
            v-if="props.content?.notificationType === 'success'"
            class="w-5 h-5 fill-green-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
        >
            <!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc. -->
            <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
        </svg>
        <svg
            v-if="props.content?.notificationType === 'error'"
            class="w-8 h-8 fill-error"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
        >
            <!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc. -->
            <close-icon class="w-8 h-8 fill-red-500"/>
        </svg>
            <div class="text-sm font-normal">{{ props.content?.message }}</div>
            <button v-show="hasHovered" @click="close" class="absolute right-2 top-2 border-none w-3 h-3 fill-indigo-700">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 384 512"
            ><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc. -->
                <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/>
            </svg>
            </button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import animejs from 'animejs'

const show = ref(false)
const hasHovered = ref(false)
const closing = ref(false)
const emit = defineEmits<{ closed: [] }>()

const toastEl = ref<HTMLElement>()

onMounted(async () => {
    show.value = true
    await nextTick()
    showAnimation()
    await startHideTimer()
})


const props = defineProps({
    content: Object,
    is_notification: Boolean,
    notification_id: String
})

const hasMousedOver = () => {
    hasHovered.value = true
}

const close = () => {
    if (closing.value) return
    closing.value = true
    animejs({
        targets: toastEl.value,
        opacity: [1, 0],
        translateX: [0, '50px'],
        height: 0,
        padding: 0,
        duration: 150,
        easing: 'easeOutCubic',
        complete: () => {
            show.value = false
            emit('closed')
        }
    })
}

const showAnimation = () => {
    animejs({
        targets: toastEl.value,
        opacity: [0, 1],
        translateX: ['50px', '0px'],
        duration: 150,
        easing: 'easeOutCubic'
    })
}

const startHideTimer = async () => {
    await wait(8000)
    if (hasHovered.value) return
    close()

}

function wait(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

defineExpose({
    close
})

</script>


<style lang="scss" scoped>
.toast-prominent-effect {
    --toast-prominent-color: var(--color-info);

    position: fixed;
    z-index: 40;
    inset: 0;
    pointer-events: none;
    box-shadow:
        inset 0 0 clamp(2.5rem, 6vw, 5rem)
            color-mix(in oklch, var(--toast-prominent-color) 30%, transparent),
        inset 0 0 clamp(6rem, 14vw, 11rem)
            color-mix(in oklch, var(--toast-prominent-color) 20%, transparent);
    animation: toast-prominent-glow 1.6s ease-in-out forwards;
}

.toast-prominent-effect--success {
    --toast-prominent-color: var(--color-success);
}

.toast-prominent-effect--warning {
    --toast-prominent-color: var(--color-warn);
}

.toast-prominent-effect--error {
    --toast-prominent-color: var(--color-error);
}

@keyframes toast-prominent-glow {
    from {
        opacity: 0;
        transform: scale(1);
    }
    35% {
        opacity: 1;
    }
    to {
        opacity: 0;
        transform: scale(1.015);
    }
}

@media (prefers-reduced-motion: reduce) {
    .toast-prominent-effect {
        animation-duration: 1ms;
    }
}

.fadePop-enter-active,
.fadePop-leave-active {
    transition: all 0.15s ease;
    // transition: transform 0.5s ease;
}

.fadePop-enter-from,
.fadePop-leave-to {
    opacity: 0;
    transform: translateX(50px)
}
</style>
